import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { Input, RadioCircle, RadioInput, Textarea } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';
import { getOptimalFontSize } from '@/utils/coverImages/textDistribution';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import SimpleUploadedImageRow from '../SimpleUploadedImageRow';
import UploadMultipleImages from '../UploadMultipleImages';
import UploadSingleImage from '../UploadSingleImage';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  LabelText,
  MoreImages,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import {
  ImageGrid,
  ImagesBox,
  TextAreaBox,
  TextParametersBox,
} from '../VariationTwo';
import { getWatermarkPosition, getWatermarkTag } from '../shared';
import {
  ActionBox,
  ActionTitle,
  StyledRadiosLabel,
} from '../shared/styledComponents';
import GeneratedImages from './GeneratedImages';
import UploadedImageRow from './UploadedImageRow';
import VariationsStyles from './VariationsStyles';

export const CHANNEL_NAME_COLORS = ['black', 'blue'];

export const CHANNEL_IMAGE_OPTIONS = ['custom', 'reddit'];

export interface ImageVariation {
  leftImageSrc: string;
  leftWatermarkTag: string;
  leftWatermarkPosition: string;
  divider: string;
  leftText: string;
  leftFontSize?: number;
  leftBrakes: boolean;
  leftAuthorName: string;
  leftAuthorImage?: string;
  leftChannelNameColor: string;
  leftChannelImageOption: string;
  rightText: string;
  authorImage?: string;
  authorName: string;
  channelNameColor: string;
  brakes: boolean;
  fontSize?: number;
  channelImageOption: string;
  width: 'half-width' | 'more-width';
}

const AdditionalBox = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  grid-column: span 2;
`;

const RadioGroup = styled.div`
  width: 240px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StyledTextArea = styled(Textarea)`
  height: 86px;
`;

const NameTextArea = styled(Textarea)`
  height: 46px;
`;

const UploadAuthorImages = styled(UploadSingleImage)`
  height: 98px;
`;

const ChannelImageHolder = styled.div`
  margin-top: 16px;
`;

const ChannelNameHolder = styled.div`
  margin-top: 16px;
`;

const HeadingsHolder = styled.div`
  flex: 1;
`;

const StyledImagesBox = styled(ImagesBox)`
  grid-column: span 2;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

getOptimalFontSize('some random text', {
  maxWidth: 530,
  maxHeight: 560,
  fontFamily: 'CustomArial',
});

interface Props {
  inArticle?: boolean;
}

const VariationImgCommentComment: FC<Props> = ({ inArticle }) => {
  const [leftImages, setLeftImages] = useState<ImageData[]>([]);
  const [showMoreImagesModal, setShowMoreImagesModal] = useState(false);
  const [width, setWidth] = useState<'half-width' | 'more-width'>('half-width');
  const [leftText, setLeftText] = useState('');
  const [leftFontSize, setLeftFontSize] = useState('');
  const [leftBrakes, setLeftBrakes] = useState(false);
  const [leftNameColor, setLeftNameColor] = useState('black');
  const [leftChannelImageOption, setLeftChannelImageOption] =
    useState('custom');
  const [leftAuthorName, setLeftAuthorName] = useState('');
  const [leftAuthorImages, setLeftAuthorImages] = useState<ImageData[]>([]);

  const [showContent, setShowContent] = useState(true);
  const [brakes, setBrakes] = useState(false);
  const [text, setText] = useState('');
  const [fontSize, setFontSize] = useState('');

  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [authorImages, setAuthorImages] = useState<ImageData[]>([]);
  const [channelImageOption, setChannelImageOption] = useState('custom');

  const [authorName, setAuthorName] = useState('');
  const [nameColor, setNameColor] = useState('black');
  const [images, setImages] = useState<string[]>();

  const onReset = () => {
    setVariations([]);
    setImages([]);
    setAuthorImages([]);
    setBrakes(false);
    setText('');
    setAuthorName('');
    setFontSize('');
    setNameColor('black');
    setChannelImageOption('custom');
    setLeftText('');
    setLeftFontSize('');
    setLeftBrakes(false);
    setLeftAuthorName('');
    setLeftAuthorImages([]);
    setLeftNameColor('black');
    setLeftChannelImageOption('custom');
  };

  const generateVariations = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const newVariations = leftImages.map((leftImage) => ({
      leftImageSrc:
        leftImage.croppedImage ?? URL.createObjectURL(leftImage.image),
      leftWatermarkTag: getWatermarkTag(leftImage),
      leftWatermarkPosition: getWatermarkPosition(leftImage),
      divider: leftImage.divider,
      leftText: leftText,
      leftFontSize: parseInt(leftFontSize),
      leftBrakes: leftBrakes,
      leftAuthorName: leftAuthorName,
      leftAuthorImage: !!leftAuthorImages.length
        ? leftAuthorImages[0]?.croppedImage ??
          URL.createObjectURL(leftAuthorImages[0].image)
        : undefined,
      leftChannelNameColor: leftNameColor,
      leftChannelImageOption: leftChannelImageOption,
      rightText: text,
      brakes: brakes,
      fontSize: parseInt(fontSize),
      authorName: authorName,
      authorImage: !!authorImages.length
        ? authorImages[0]?.croppedImage ??
          URL.createObjectURL(authorImages[0].image)
        : undefined,
      channelNameColor: nameColor,
      channelImageOption: channelImageOption,
      width,
    }));

    setVariations(newVariations);
  };

  const renderLeftImages = leftImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setLeftImages}
      moreWidth={width === 'more-width'}
    />
  ));

  const renderAuthorImages = authorImages.map((image, index) => (
    <SimpleUploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setAuthorImages}
    />
  ));

  const renderLeftAuthorImages = leftAuthorImages.map((image, index) => (
    <SimpleUploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setLeftAuthorImages}
    />
  ));

  const isResetDisabled =
    !fontSize &&
    !text &&
    !brakes &&
    !authorImages.length &&
    !authorName &&
    nameColor === 'black' &&
    channelImageOption === 'custom' &&
    !leftText &&
    !leftFontSize &&
    !leftBrakes &&
    !leftAuthorImages.length &&
    !leftAuthorName &&
    leftNameColor === 'black' &&
    leftChannelImageOption === 'custom';

  return (
    <>
      <Box>
        <Header>
          <HeadingsHolder>
            <Heading $type="button">Image + Comment + Comment</Heading>
            <Text $type="smallHeadline" $color="gray">
              Attack image on the left side and two comments with the necessary
              parameters. Additionally, include the author's image and name
            </Text>
          </HeadingsHolder>
          <HeaderButtons>
            <ResetButton
              color="red"
              disabled={isResetDisabled}
              onClick={onReset}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <ImageGrid>
            <StyledImagesBox $hasImages={!!leftImages?.length}>
              <LabelText $type="smallHeadline">Left Images</LabelText>
              {!!leftImages?.length ? (
                <>
                  <UploadedImages>{renderLeftImages}</UploadedImages>
                  <MoreImages>
                    <AttachedCountBadge count={leftImages.length} />
                    <AddMoreButton
                      onClick={() => setShowMoreImagesModal(true)}
                      color="colorized"
                    >
                      Add more images
                    </AddMoreButton>
                  </MoreImages>
                </>
              ) : (
                <UploadMultipleImages
                  coverIndex={2}
                  noneDivider
                  setImages={setLeftImages}
                />
              )}
            </StyledImagesBox>
            <TextParametersBox>
              <TextAreaBox>
                <div>
                  <LabelText $type="smallHeadline">Text</LabelText>
                  <StyledTextArea
                    onChange={(e) => setLeftText(e.target.value)}
                    value={leftText}
                    placeholder="Add text"
                  ></StyledTextArea>
                </div>
                <div>
                  <LabelText $type="smallHeadline">Font Size</LabelText>
                  <Input
                    value={leftFontSize}
                    onChange={(e) => setLeftFontSize(e.target.value)}
                    placeholder="Font Size (px)"
                    type="number"
                    min="0"
                  />
                </div>
                <div>
                  <ActionTitle $type="smallHeadline">
                    Text Parameters
                  </ActionTitle>
                  <ActionBox>
                    <Checkbox
                      checked={leftBrakes}
                      onChange={() => setLeftBrakes((prev) => !prev)}
                    >
                      Custom brakes
                    </Checkbox>
                  </ActionBox>
                </div>
                <div>
                  <LabelText $type="smallHeadline">Channel Image</LabelText>
                  <ActionBox>
                    {CHANNEL_IMAGE_OPTIONS.map((option) => (
                      <StyledRadiosLabel key={option}>
                        {option}
                        <RadioInput
                          type="radio"
                          name="lef_channel_image"
                          checked={leftChannelImageOption === option}
                          onChange={() => setLeftChannelImageOption(option)}
                        />
                        <RadioCircle />
                      </StyledRadiosLabel>
                    ))}
                  </ActionBox>
                  {leftChannelImageOption === 'custom' && (
                    <ChannelImageHolder>
                      <LabelText $type="smallHeadline">
                        Add Channel Image
                      </LabelText>
                      {!!leftAuthorImages?.length ? (
                        <UploadedImages>
                          {renderLeftAuthorImages}
                        </UploadedImages>
                      ) : (
                        <UploadAuthorImages
                          coverIndex={2}
                          setImages={setLeftAuthorImages}
                        />
                      )}
                    </ChannelImageHolder>
                  )}
                </div>
                <div>
                  <LabelText $type="smallHeadline">
                    Channel Name Color
                  </LabelText>
                  <ActionBox>
                    {CHANNEL_NAME_COLORS.map((color) => (
                      <StyledRadiosLabel key={color}>
                        {color}
                        <RadioInput
                          type="radio"
                          name="left_name_color"
                          checked={leftNameColor === color}
                          onChange={() => setLeftNameColor(color)}
                        />
                        <RadioCircle />
                      </StyledRadiosLabel>
                    ))}
                  </ActionBox>
                  <ChannelNameHolder>
                    <LabelText $type="smallHeadline">Channel Name</LabelText>
                    <NameTextArea
                      placeholder="Add Name"
                      onChange={(e) => setLeftAuthorName(e.target.value)}
                      value={leftAuthorName}
                    ></NameTextArea>
                  </ChannelNameHolder>
                </div>
              </TextAreaBox>
            </TextParametersBox>
            <TextParametersBox>
              <TextAreaBox>
                <div>
                  <LabelText $type="smallHeadline">Text</LabelText>
                  <StyledTextArea
                    onChange={(e) => setText(e.target.value)}
                    value={text}
                    placeholder="Add text"
                  ></StyledTextArea>
                </div>
                <div>
                  <LabelText $type="smallHeadline">Font Size</LabelText>
                  <Input
                    value={fontSize}
                    onChange={(e) => setFontSize(e.target.value)}
                    placeholder="Font Size (px)"
                    type="number"
                    min="0"
                  />
                </div>
                <div>
                  <ActionTitle $type="smallHeadline">
                    Text Parameters
                  </ActionTitle>
                  <ActionBox>
                    <Checkbox
                      checked={brakes}
                      onChange={() => setBrakes((prev) => !prev)}
                    >
                      Custom brakes
                    </Checkbox>
                  </ActionBox>
                </div>
                <div>
                  <LabelText $type="smallHeadline">Channel Image</LabelText>
                  <ActionBox>
                    {CHANNEL_IMAGE_OPTIONS.map((option) => (
                      <StyledRadiosLabel key={option}>
                        {option}
                        <RadioInput
                          type="radio"
                          name="channel_image"
                          checked={channelImageOption === option}
                          onChange={() => setChannelImageOption(option)}
                        />
                        <RadioCircle />
                      </StyledRadiosLabel>
                    ))}
                  </ActionBox>
                  {channelImageOption === 'custom' && (
                    <ChannelImageHolder>
                      <LabelText $type="smallHeadline">
                        Add Channel Image
                      </LabelText>
                      {!!authorImages?.length ? (
                        <UploadedImages>{renderAuthorImages}</UploadedImages>
                      ) : (
                        <UploadAuthorImages
                          coverIndex={2}
                          setImages={setAuthorImages}
                        />
                      )}
                    </ChannelImageHolder>
                  )}
                </div>
                <div>
                  <LabelText $type="smallHeadline">
                    Channel Name Color
                  </LabelText>
                  <ActionBox>
                    {CHANNEL_NAME_COLORS.map((color) => (
                      <StyledRadiosLabel key={color}>
                        {color}
                        <RadioInput
                          type="radio"
                          name="name_color"
                          checked={nameColor === color}
                          onChange={() => setNameColor(color)}
                        />
                        <RadioCircle />
                      </StyledRadiosLabel>
                    ))}
                  </ActionBox>
                  <ChannelNameHolder>
                    <LabelText $type="smallHeadline">Channel Name</LabelText>
                    <NameTextArea
                      placeholder="Add Name"
                      onChange={(e) => setAuthorName(e.target.value)}
                      value={authorName}
                    ></NameTextArea>
                  </ChannelNameHolder>
                </div>
              </TextAreaBox>
            </TextParametersBox>
            <AdditionalBox>
              <Text $type="smallHeadline" $color="gray">
                Additional:
              </Text>

              <RadioGroup>
                <StyledRadiosLabel key="half-width">
                  50/50
                  <RadioInput
                    type="radio"
                    name="width"
                    checked={width === 'half-width'}
                    onChange={() => setWidth('half-width')}
                  />
                  <RadioCircle />
                </StyledRadiosLabel>
                <StyledRadiosLabel key="more-width">
                  40/60
                  <RadioInput
                    type="radio"
                    name="width"
                    checked={width === 'more-width'}
                    onChange={() => setWidth('more-width')}
                  />
                  <RadioCircle />
                </StyledRadiosLabel>
              </RadioGroup>
            </AdditionalBox>
          </ImageGrid>
        )}
      </Box>
      <VariationsStyles />
      {showContent && (
        <ButtonHolder>
          <Button onClick={generateVariations}>
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
      <AnimatePresence>
        {!!showMoreImagesModal && (
          <MoreImagesModal
            onClose={() => setShowMoreImagesModal(false)}
            setImages={setLeftImages}
            coverIndex={1}
            noneDivider
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationImgCommentComment;
